import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  operator,
  type OperatorAutoreasonRunSummary,
  type OperatorAutoreasonRunDetail,
} from '../lib/api.js';
import { Pill, type PillTone } from '../components/ui.js';

// Read-only operator console for autoreason runs. Surfaces the Phase 4c
// critic prohibition_signals (drift_score + amend_directive) so operators
// can see when the engine still drifts after the semantic enforcement
// layer landed.
//
// Re-fire is intentionally NOT a 1-click HTTP action in v1 — operator
// copies a CLI command from the drill-down view and runs it locally.
// Reasoning:
//   - The launch path needs createLiveLLMRouter + DataForSEO client +
//     publish actor wiring that's wired into the daemon process, not
//     the bloom-api process. Triggering launchPhaseCRun from inside an
//     HTTP handler would either block the request for minutes or fire-
//     and-forget into a process that can't observe the failure.
//   - --fragment-intent is a section-purpose anchor, not a directive
//     injector. Letting the operator paste + edit the command before
//     running keeps engine semantics honest.
// v2 lands a 1-click path once Phase C real-publish actor finalization
// gives us a daemon-side queue + the publish actors are wired.

type SortKey = 'started' | 'cost' | 'iters' | 'drift' | 'status';

export function OperatorAutoreasonRunsPage() {
  const list = useQuery({
    queryKey: ['operator', 'autoreason', 'runs'],
    queryFn: operator.autoreasonRuns,
    staleTime: 30_000,
  });

  const [sort, setSort] = useState<SortKey>('started');
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');
  const [activeId, setActiveId] = useState<string | null>(null);

  const rows = useMemo(() => {
    const list_ = list.data?.runs ?? [];
    return [...list_].sort((a, b) => {
      const av = sortKey(a, sort);
      const bv = sortKey(b, sort);
      if (av === bv) return 0;
      const cmp = av < bv ? -1 : 1;
      return dir === 'asc' ? cmp : -cmp;
    });
  }, [list.data, sort, dir]);

  const onSort = (key: SortKey): void => {
    if (sort === key) setDir(dir === 'asc' ? 'desc' : 'asc');
    else { setSort(key); setDir('desc'); }
  };

  const total = rows.length;
  const drifting = rows.filter((r) => (r.latest_drift_score ?? 0) >= 0.30).length;
  const pending = rows.filter((r) => r.publish_state === 'pending_operator_confirmation').length;

  return (
    <div className="space-y-12">
      <section>
        <p className="text-xs uppercase tracking-[0.28em] text-cream/40 mb-5">
          Operator Console
        </p>
        <h1 className="font-serif text-5xl md:text-6xl leading-[0.95] tracking-tightest text-cream">
          Autoreason runs
        </h1>
        <p className="mt-5 text-base text-cream/55 max-w-2xl">
          Every autoreason run + the latest critic's prohibition drift signal.
          Drill into a run to see the iteration history and grab a re-fire
          CLI command pre-filled with the source run's amendment directive.
        </p>
      </section>

      <section>
        <div className="grid grid-cols-3 gap-4">
          <Big label="Runs" value={total} />
          <Big
            label="Still drifting"
            value={drifting}
            tone={drifting > 0 ? 'warn' : 'ok'}
            hint="latest drift_score ≥ 0.30"
          />
          <Big
            label="Pending confirm"
            value={pending}
            tone={pending > 0 ? 'urgent' : 'ok'}
            hint="awaiting operator publish approval"
          />
        </div>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3">
          <h2 className="text-[10px] uppercase tracking-[0.28em] text-cream/35 mb-5">
            Recent runs
          </h2>

          {list.isLoading && <div className="text-sm text-cream/40">Loading runs…</div>}
          {list.isError && (
            <div className="rounded-2xl border border-cream/10 bg-cream/[0.02] p-6 text-sm text-cream/55">
              Couldn't load autoreason runs. Refresh in a moment.
            </div>
          )}

          {list.data && rows.length === 0 && (
            <div className="rounded-2xl border border-cream/10 bg-cream/[0.02] p-8 text-center text-cream/55 text-sm">
              No autoreason runs yet.
            </div>
          )}

          {list.data && rows.length > 0 && (
            <div className="rounded-2xl border border-cream/10 bg-cream/[0.02] overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-[0.18em] text-cream/35 border-b border-cream/[0.08]">
                    <Th sort={sort} dir={dir} onSort={onSort} sortKey="started" align="left">Started</Th>
                    <Th sort={sort} dir={dir} onSort={onSort} sortKey="status" align="left">Run</Th>
                    <Th sort={sort} dir={dir} onSort={onSort} sortKey="iters" align="right">Iters</Th>
                    <Th sort={sort} dir={dir} onSort={onSort} sortKey="drift" align="right">Drift</Th>
                    <Th sort={sort} dir={dir} onSort={onSort} sortKey="cost" align="right">Cost</Th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.run_id}
                      onClick={() => setActiveId(r.run_id)}
                      className={`border-b border-cream/[0.05] cursor-pointer transition-colors ${
                        activeId === r.run_id ? 'bg-cream/[0.06]' : 'hover:bg-cream/[0.02]'
                      }`}
                    >
                      <td className="px-5 py-4 text-cream/55 text-xs whitespace-nowrap">
                        {formatRelativeDate(r.started_at)}
                      </td>
                      <td className="px-5 py-4">
                        <div className="font-medium text-cream truncate max-w-xs">{r.target_query}</div>
                        <div className="mt-0.5 text-[11px] text-cream/40 truncate max-w-xs">
                          {hostFromUrl(r.target_url)}
                        </div>
                        <div className="mt-1 flex gap-1.5">
                          <Pill tone={runStatusTone(r.status)}>{r.status}</Pill>
                          {r.publish_state && (
                            <Pill tone={publishStateTone(r.publish_state)}>{r.publish_state.replace(/_/g, ' ')}</Pill>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-4 text-right tabular-nums text-cream/75">
                        {r.iteration_count}
                      </td>
                      <td className="px-5 py-4 text-right tabular-nums">
                        <DriftCell value={r.latest_drift_score} />
                      </td>
                      <td className="px-5 py-4 text-right tabular-nums text-cream/75">
                        ${r.cost_usd.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="lg:col-span-2">
          <h2 className="text-[10px] uppercase tracking-[0.28em] text-cream/35 mb-5">
            {activeId ? 'Detail' : 'Pick a run'}
          </h2>
          {activeId
            ? <RunDetail runId={activeId} />
            : (
              <div className="rounded-2xl border border-dashed border-cream/[0.18] bg-cream/[0.02] p-8 text-center text-cream/55 text-sm">
                Click a row on the left to inspect iterations + grab a re-fire CLI command.
              </div>
            )}
        </div>
      </section>
    </div>
  );
}

function RunDetail({ runId }: { runId: string }) {
  const q = useQuery({
    queryKey: ['operator', 'autoreason', 'run', runId],
    queryFn: () => operator.autoreasonRun(runId),
    staleTime: 30_000,
  });

  if (q.isLoading) return <div className="text-sm text-cream/40">Loading…</div>;
  if (q.isError) {
    return (
      <div className="rounded-2xl border border-rose-300/20 bg-rose-500/[0.04] p-5 text-sm text-rose-200">
        Couldn't load this run.
      </div>
    );
  }
  const run = q.data?.run;
  if (!run) return null;

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-cream/10 bg-cream/[0.02] p-5 space-y-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-cream/40">Target</div>
          <div className="mt-1 font-medium text-cream break-all">{run.target_url}</div>
          <div className="mt-1 text-sm text-cream/60">"{run.target_query}"</div>
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <Field label="Status" value={run.status} />
          <Field label="Page type" value={run.page_type} />
          <Field label="Iterations" value={String(run.iteration_count)} />
          <Field label="Cost" value={`$${run.cost_usd.toFixed(2)}`} />
          {run.publish_state && (
            <Field label="Publish" value={run.publish_state.replace(/_/g, ' ')} />
          )}
          {run.published_at && (
            <Field label="Published" value={formatRelativeDate(run.published_at)} />
          )}
        </div>
        {run.publish_error && (
          <div className="rounded-xl border border-rose-300/20 bg-rose-500/[0.04] px-3 py-2 text-xs text-rose-200">
            {run.publish_error}
          </div>
        )}
      </div>

      <RefireCommand run={run} />

      <div>
        <h3 className="text-[10px] uppercase tracking-[0.18em] text-cream/40 mb-3">
          Iterations
        </h3>
        <div className="space-y-3">
          {run.iterations.map((iter) => (
            <div
              key={iter.iteration_num}
              className="rounded-xl border border-cream/10 bg-cream/[0.02] p-4"
            >
              <div className="flex items-center justify-between">
                <span className="text-cream font-medium">Iter {iter.iteration_num}</span>
                <DriftCell value={iter.drift_score} />
              </div>
              {iter.amend_directive && (
                <div className="mt-2 text-sm text-cream/75 italic">
                  "{iter.amend_directive}"
                </div>
              )}
              {iter.drift_examples.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {iter.drift_examples.map((ex, i) => (
                    <span
                      key={i}
                      className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/[0.12] text-amber-200 border border-amber-300/[0.20]"
                    >
                      {ex}
                    </span>
                  ))}
                </div>
              )}
              <div className="mt-3 grid grid-cols-3 gap-2 text-[11px] text-cream/45">
                <span>${iter.iteration_cost_usd.toFixed(3)}</span>
                <span>{(iter.duration_ms / 1000).toFixed(1)}s</span>
                <span>{iter.prohibition_violations_count} regex hits</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function RefireCommand({ run }: { run: OperatorAutoreasonRunDetail }) {
  const [copied, setCopied] = useState(false);
  const [draftDirective, setDraftDirective] = useState(run.latest_amend_directive ?? '');

  const cmd = buildRefireCli({
    target_url: run.target_url,
    target_query: run.target_query,
    self_target: selfTargetForUrl(run.target_url),
    fragment_intent: draftDirective.trim() || null,
  });

  const onCopy = (): void => {
    void navigator.clipboard.writeText(cmd).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <div className="rounded-2xl border border-cream/10 bg-cream/[0.02] p-5 space-y-3">
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-cream/40">
          Re-fire CLI
        </div>
        <div className="mt-1 text-xs text-cream/55">
          Edit the directive, copy the command, paste it into the bloom worktree
          on your terminal. <code className="text-cream/75">--fragment-intent</code> is a
          section-purpose anchor (fragment-mode runs only) — the engine reads it as
          rhetorical context, not a hard directive. Default mode is dry-run audit; add
          <code className="text-cream/75"> --prod-publish</code> when ready.
        </div>
      </div>

      <textarea
        value={draftDirective}
        onChange={(e) => setDraftDirective(e.target.value)}
        rows={3}
        placeholder={run.latest_amend_directive ?? 'No amend directive on the source run.'}
        className="w-full text-sm bg-black/40 text-cream/85 border border-cream/[0.14] rounded-lg px-3 py-2 font-mono leading-relaxed focus:border-cream/40 focus:outline-none"
      />

      <pre className="text-[11px] text-cream/75 bg-black/40 border border-cream/[0.10] rounded-lg p-3 overflow-x-auto whitespace-pre leading-relaxed">{cmd}</pre>

      <button
        onClick={onCopy}
        className="w-full text-xs uppercase tracking-[0.16em] py-2.5 rounded-lg bg-cream/[0.10] hover:bg-cream/[0.16] text-cream/95 transition-colors font-semibold"
      >
        {copied ? '✓ Copied' : 'Copy command'}
      </button>
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────

interface RefireCliInput {
  target_url: string;
  target_query: string;
  self_target: string | null;
  fragment_intent: string | null;
}

function buildRefireCli(input: RefireCliInput): string {
  const lines = [
    'npx tsx scripts/autoreason-phase-c-launch.ts \\',
    `  --target-url ${input.target_url} \\`,
    `  --target-query ${shellQuote(input.target_query)} \\`,
  ];
  if (input.self_target) {
    lines.push(`  --self-target ${input.self_target} \\`);
  }
  if (input.fragment_intent) {
    lines.push(`  --fragment-intent ${shellQuote(input.fragment_intent)}`);
  } else {
    // Drop the trailing backslash on whichever line was last
    const last = lines[lines.length - 1]!;
    lines[lines.length - 1] = last.replace(/ \\$/, '');
  }
  return lines.join('\n');
}

function shellQuote(s: string): string {
  // Single-quote and escape any embedded single quotes via the
  // ' "'" ' trick; keeps the engine's single-quote-only literal
  // expectation (per feedback_engine_fragment_blindspots.md).
  return `'${s.replace(/'/g, `'"'"'`)}'`;
}

function selfTargetForUrl(url: string): string | null {
  try {
    const host = new URL(url).hostname.replace(/^www\./, '');
    if (host === 'businessisbloomin.com') return 'bloom_self';
    if (host === 'holocronhq.com') return 'holocron';
    return null;
  } catch {
    return null;
  }
}

function hostFromUrl(url: string): string {
  try { return new URL(url).hostname.replace(/^www\./, ''); }
  catch { return url; }
}

function sortKey(r: OperatorAutoreasonRunSummary, key: SortKey): string | number {
  switch (key) {
    case 'started': return r.started_at;
    case 'cost':    return r.cost_usd;
    case 'iters':   return r.iteration_count;
    case 'drift':   return r.latest_drift_score ?? -1;
    case 'status':  return r.status;
  }
}

function runStatusTone(status: string): PillTone {
  switch (status) {
    case 'converged':
    case 'complete':       return 'ok';
    case 'running':
    case 'watch_window':   return 'info';
    case 'max_iter':
    case 'stalled':        return 'warn';
    case 'flagged_low_agreement':
    case 'failed':
    case 'reverted':       return 'bad';
    default:               return 'neutral';
  }
}

function publishStateTone(state: string): PillTone {
  switch (state) {
    case 'published':                       return 'ok';
    case 'pending_operator_confirmation':   return 'warn';
    case 'rejected':                        return 'neutral';
    case 'publish_failed':
    case 'verify_failed':                   return 'bad';
    default:                                return 'neutral';
  }
}

function DriftCell({ value }: { value: number | null }) {
  if (value == null) return <span className="text-cream/30">—</span>;
  const tone =
    value >= 0.50 ? 'text-rose-300'
    : value >= 0.30 ? 'text-amber-300'
    : 'text-emerald-300/90';
  return <span className={`font-serif text-base ${tone}`}>{value.toFixed(2)}</span>;
}

function Th({
  children, sort, dir, sortKey: thKey, onSort, align,
}: {
  children: React.ReactNode;
  sort: SortKey;
  dir: 'asc' | 'desc';
  sortKey: SortKey;
  onSort: (k: SortKey) => void;
  align: 'left' | 'right';
}) {
  const active = sort === thKey;
  return (
    <th className={`px-5 py-3 ${align === 'right' ? 'text-right' : 'text-left'} font-medium`}>
      <button
        onClick={() => onSort(thKey)}
        className={`inline-flex items-center gap-1 hover:text-cream transition-colors ${active ? 'text-cream' : ''}`}
      >
        {children}
        {active && <span className="text-[8px]">{dir === 'asc' ? '↑' : '↓'}</span>}
      </button>
    </th>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-cream/40">{label}</div>
      <div className="mt-0.5 text-cream/85 capitalize">{value}</div>
    </div>
  );
}

function Big({
  label, value, tone = 'neutral', hint,
}: {
  label: string;
  value: number;
  tone?: 'ok' | 'warn' | 'urgent' | 'neutral';
  hint?: string;
}) {
  const numClass =
    tone === 'urgent' ? 'text-red-400'
    : tone === 'warn' ? 'text-amber-300'
    : tone === 'ok' && value > 0 ? 'text-emerald-300'
    : 'text-cream';
  return (
    <div className="rounded-2xl border border-cream/10 bg-cream/[0.02] p-6">
      <div className="text-[10px] uppercase tracking-[0.24em] text-cream/40">{label}</div>
      <div className={`mt-2 font-serif text-5xl tabular-nums leading-none tracking-tightest ${numClass}`}>{value}</div>
      {hint && <div className="mt-2 text-[11px] text-cream/40">{hint}</div>}
    </div>
  );
}

function formatRelativeDate(iso: string): string {
  const d = new Date(iso);
  const now = Date.now();
  const diffMs = now - d.getTime();
  if (diffMs < 0) return 'in future';
  if (diffMs < 60_000) return 'just now';
  if (diffMs < 60 * 60_000) return `${Math.floor(diffMs / 60_000)}m ago`;
  if (diffMs < 24 * 60 * 60_000) return `${Math.floor(diffMs / (60 * 60_000))}h ago`;
  const diffDays = Math.floor(diffMs / (24 * 60 * 60_000));
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
